import React, { useState, useEffect, useCallback, useRef } from 'react'
import { BCCWJMemoryStatus, BCCWJWord, Deck } from '../types'
import { apiClient } from '../api/client'
import {
  BCCWJ_MEMORY_STATUS_LABELS,
  BCCWJ_UNRATED_LABEL,
  statusLabel,
  statusBadgeClass,
  BCCWJ_STATUS_FILTER_OPTIONS,
  BCCWJ_STATUS_RADIO_OPTIONS,
} from '../constants/bccwjStatus'
import './BCCWJBrowser.css'

function mapPickerError(err: any): string {
  const status = err?.response?.status
  if (status === 409) return 'このデッキには既に追加されています'
  if (status === 403) return 'このデッキに追加する権限がありません'
  if (status === 404) return 'デッキが見つかりません'
  return 'デッキへの追加に失敗しました'
}

const BCCWJBrowser: React.FC = () => {
  const [words, setWords] = useState<BCCWJWord[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedPos, setSelectedPos] = useState('')
  const [posTypes, setPosTypes] = useState<string[]>([])
  const [selectedWord, setSelectedWord] = useState<BCCWJWord | null>(null)
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false)
  const [offset, setOffset] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [totalWords, setTotalWords] = useState<number | null>(null)
  const [totalHits, setTotalHits] = useState<number | null>(null)

  // Memory-status filter (values 'none' and '1'-'4') and per-level stats
  const [statusFilter, setStatusFilter] = useState<Set<string>>(new Set())
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({})
  const [statusError, setStatusError] = useState<string | null>(null)

  // Add-to-deck state
  const [decks, setDecks] = useState<Deck[]>([])
  const [deckPickerWord, setDeckPickerWord] = useState<BCCWJWord | null>(null)
  const [selectedDeckId, setSelectedDeckId] = useState('')
  const [adding, setAdding] = useState(false)
  const [pickerError, setPickerError] = useState<string | null>(null)
  const [addedWordIds, setAddedWordIds] = useState<Set<string>>(new Set())
  const [confirmMessage, setConfirmMessage] = useState<string | null>(null)
  const confirmTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Search requests bump this sequence; a response from a superseded request is dropped
  const searchSeqRef = useRef(0)
  // Key of the query currently displayed ('||' = the common-words view)
  const lastQueryKeyRef = useRef('||')
  // Criteria of the query currently on screen. runSearch captures them when a
  // search actually fires, so もっと見る pages through the displayed query even
  // if the silent controls (text input, checkboxes) changed since.
  const appliedQueryRef = useRef({ search: '', pos: '', status: '' })

  const LIMIT = 20

  const loadCommonWords = async () => {
    const seq = ++searchSeqRef.current
    setLoading(true)
    setError(null)
    appliedQueryRef.current = { search: '', pos: '', status: '' }
    try {
      const data = await apiClient.getCommonBCCWJWords(50)
      if (seq !== searchSeqRef.current) return
      setWords(data)
      setOffset(0)
      setHasMore(false)
      setTotalHits(null)
    } catch (err: any) {
      console.error('Error loading BCCWJ words:', err)
      if (seq !== searchSeqRef.current) return
      setError(err.message || '単語を読み込めませんでした')
    } finally {
      if (seq === searchSeqRef.current) setLoading(false)
    }
  }

  const loadPosTypes = async () => {
    try {
      const types = await apiClient.getBCCWJPosTypes()
      setPosTypes(types)
    } catch (err) {
      console.error('Error loading POS types:', err)
    }
  }

  const loadTotalWords = async () => {
    try {
      const stats = await apiClient.getBCCWJStats()
      setTotalWords(stats.total_words)
      setStatusCounts(stats.status_counts ?? {})
    } catch (err) {
      console.error('Error loading BCCWJ stats:', err)
    }
  }

  const loadDecks = async () => {
    try {
      const res = await apiClient.getDecks()
      const list = (res as any)?.data
      setDecks(Array.isArray(list) ? (list as Deck[]) : [])
    } catch (err) {
      console.error('Error loading decks:', err)
      setDecks([])
    }
  }

  useEffect(() => {
    loadCommonWords()
    loadPosTypes()
    loadTotalWords()
    loadDecks()
  }, [])

  const doSearch = useCallback(async (
    search: string,
    pos: string,
    newOffset: number = 0,
    append: boolean = false,
    statusParam?: string
  ) => {
    const seq = ++searchSeqRef.current
    if (!append) setLoading(true)
    else setLoadingMore(true)
    setError(null)

    try {
      const response = await apiClient.searchBCCWJWords(
        search || undefined,
        undefined,
        pos || undefined,
        undefined,
        LIMIT,
        newOffset,
        statusParam || undefined
      )
      if (seq !== searchSeqRef.current) return
      if (append) {
        setWords(prev => [...prev, ...response.words])
      } else {
        setWords(response.words)
      }
      setOffset(newOffset)
      setTotalHits(response.total)
      setHasMore(newOffset + response.words.length < response.total)
    } catch (err: any) {
      console.error('Error searching BCCWJ words:', err)
      if (seq !== searchSeqRef.current) return
      setError(err.message || '単語を検索できませんでした')
    } finally {
      if (seq === searchSeqRef.current) {
        setLoading(false)
        setLoadingMore(false)
      }
    }
  }, [])

  const statusParam = () => Array.from(statusFilter).sort().join(',')

  // The text search fires via the 検索 button / form submit, not per keystroke;
  // an identical search|pos|status combination is skipped — except on an
  // explicit 検索 click, which always refetches
  const runSearch = (queryText: string, pos: string, force = false) => {
    const search = queryText.trim()
    const sp = statusParam()
    const key = `${search}|${pos}|${sp}`
    if (!force && key === lastQueryKeyRef.current) return
    lastQueryKeyRef.current = key
    appliedQueryRef.current = { search, pos, status: sp }
    if (search || pos || sp) doSearch(search, pos, 0, false, sp || undefined)
    else loadCommonWords()
  }

  // The 品詞 pulldown and the status checkboxes are criteria like the text
  // input: changing them only updates the pending filter; the request runs
  // when 検索 is pressed (form submit)
  const handleStatusFilterToggle = (value: string) => {
    setStatusFilter(prev => {
      const next = new Set(prev)
      if (next.has(value)) next.delete(value)
      else next.add(value)
      return next
    })
  }

  // Optimistic status change from the per-row selector; rolled back on failure
  const handleStatusChange = async (word: BCCWJWord, value: string) => {
    // Revert only this row, functionally: a whole-array snapshot written back
    // on failure could clobber a search that resolved while the PUT hung.
    const revertRow = () =>
      setWords(ws => ws.map(w => (
        w.id === word.id
          ? { ...w, status: word.status ?? null, check_count: word.check_count ?? null }
          : w
      )))
    const apply = (status: BCCWJMemoryStatus | null, check_count: number | null) =>
      setWords(ws => ws.map(w => (w.id === word.id ? { ...w, status, check_count } : w)))
    try {
      setStatusError(null)
      if (value === '') {
        apply(null, null)
        await apiClient.clearBCCWJWordStatus(word.id)
      } else {
        const level = Number(value) as BCCWJMemoryStatus
        apply(level, (word.check_count ?? 0) + 1)
        const res = await apiClient.setBCCWJWordStatus(word.id, level)
        apply(res.status, res.check_count)
      }
      loadTotalWords()
    } catch (err) {
      console.error('Failed to update word status:', err)
      revertRow()
      setStatusError('状態を更新できませんでした')
    }
  }

  const handleWordClick = (word: BCCWJWord) => {
    setSelectedWord(word)
    setIsDetailModalOpen(true)
  }

  const handleLoadMore = () => {
    // Continue the query on screen, not the pending criteria: a silent edit
    // (typed text, unchecked box) after the search must not redirect the page
    const applied = appliedQueryRef.current
    doSearch(applied.search, applied.pos, offset + LIMIT, true, applied.status || undefined)
  }

  const openDeckPicker = (word: BCCWJWord) => {
    setDeckPickerWord(word)
    setSelectedDeckId('')
    setPickerError(null)
  }

  const closeDeckPicker = () => {
    if (adding) return
    setDeckPickerWord(null)
  }

  const handleAddToDeck = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!deckPickerWord || !selectedDeckId || adding) return
    setAdding(true)
    setPickerError(null)
    try {
      await apiClient.createCardFromBCCWJ(selectedDeckId, deckPickerWord.id)
      setAddedWordIds(prev => new Set(prev).add(deckPickerWord.id))
      const deckName = decks.find(d => d.deck_id === selectedDeckId)?.name ?? ''
      setConfirmMessage(`「${deckPickerWord.lemma}」を「${deckName}」に追加しました`)
      if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current)
      confirmTimerRef.current = setTimeout(() => setConfirmMessage(null), 4000)
      setDeckPickerWord(null)
    } catch (err: any) {
      console.error('Error adding card to deck:', err)
      setPickerError(mapPickerError(err))
    } finally {
      setAdding(false)
    }
  }

  const renderAddButton = (word: BCCWJWord) => (
    addedWordIds.has(word.id) ? (
      <button type="button" className="bccwj-add-btn bccwj-add-btn-added" disabled>
        追加済み ✓
      </button>
    ) : (
      <button
        type="button"
        className="bccwj-add-btn"
        onClick={(e) => { e.stopPropagation(); openDeckPicker(word) }}
      >
        デッキに追加
      </button>
    )
  )

  const formatNumber = (n?: number) => {
    if (!n) return '-'
    return n.toLocaleString()
  }

  if (loading) {
    return (
      <div className="bccwj-browser">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>BCCWJ 単語を読み込み中...</p>
        </div>
      </div>
    )
  }

  if (error && words.length === 0) {
    return (
      <div className="bccwj-browser">
        <div className="error-container">
          <h2>単語の読み込みエラー</h2>
          <p>{error}</p>
          <button onClick={loadCommonWords} className="retry-btn">再試行</button>
        </div>
      </div>
    )
  }

  return (
    <div className="bccwj-browser">
      <div className="bccwj-header">
        <h1>BCCWJ 単語ブラウザ</h1>
        <p>BCCWJ コーパスの頻度順で日本語語彙を閲覧</p>
        {totalWords !== null && (
          <span className="total-words-badge">収録語数：{totalWords.toLocaleString()} 語</span>
        )}
      </div>

      <div className="filters-section">
        <form
          className="search-box"
          onSubmit={(e) => { e.preventDefault(); runSearch(searchQuery, selectedPos, true) }}
        >
          <input
            type="text"
            placeholder="単語または読みで検索..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          <button type="submit" className="search-btn">検索</button>
        </form>

        <div className="filter-controls">
          <div className="filter-group">
            <label>品詞</label>
            <select
              value={selectedPos}
              onChange={(e) => setSelectedPos(e.target.value)}
              className="filter-select"
            >
              <option value="">すべて</option>
              {posTypes.map(pos => (
                <option key={pos} value={pos}>{pos}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>状態</label>
            <div className="status-filter-group">
              {BCCWJ_STATUS_FILTER_OPTIONS.map(opt => (
                <label key={opt.value} className="status-filter-option">
                  <input
                    type="checkbox"
                    checked={statusFilter.has(opt.value)}
                    onChange={() => handleStatusFilterToggle(opt.value)}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {statusError && <div className="bccwj-status-error" role="alert">{statusError}</div>}

      {confirmMessage && (
        <div className="bccwj-confirm-toast" role="status">{confirmMessage}</div>
      )}

      <div className="bccwj-grid">
        {words.length === 0 ? (
          <div className="no-words">
            <p>単語が見つかりません。検索条件やフィルターを調整してみてください。</p>
          </div>
        ) : (
          words.map(word => (
            <div
              key={word.id}
              className="bccwj-word-card"
              onClick={() => handleWordClick(word)}
            >
              <div className="word-header">
                {word.rank && <span className="rank-badge">#{word.rank}</span>}
                {word.pos && <span className="pos-tag">{word.pos}</span>}
                <span className={`status-badge ${statusBadgeClass(word.status)}`}>
                  {statusLabel(word.status)}
                  {(word.check_count ?? 0) > 1 && ` ×${word.check_count}`}
                </span>
              </div>
              <div className="word-content">
                <div className="lemma">{word.lemma}</div>
                {word.hiragana && word.hiragana !== word.lemma && (
                  <div className="hiragana">{word.hiragana}</div>
                )}
              </div>
              <div className="word-card-footer">
                {/* stopPropagation keeps the card click from opening the detail
                    modal (click and keyboard Space both bubble a click event) */}
                <div
                  className="status-radio-group"
                  role="radiogroup"
                  aria-label={`状態: ${word.lemma}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  {BCCWJ_STATUS_RADIO_OPTIONS.map(opt => (
                    <label key={opt.value} className="status-radio-option">
                      <input
                        type="radio"
                        name={`bccwj-status-${word.id}`}
                        value={opt.value}
                        aria-label={opt.ariaLabel}
                        checked={
                          (word.status != null && [1, 2, 3, 4].includes(word.status)
                            ? String(word.status)
                            : 'none') === opt.value
                        }
                        /* Radios speak 'none' (shared with the filter/API);
                           handleStatusChange's clear signal is '' — translate here */
                        onChange={() => handleStatusChange(word, opt.value === 'none' ? '' : opt.value)}
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
                {renderAddButton(word)}
              </div>
            </div>
          ))
        )}
      </div>

      {hasMore && (
        <button
          className="load-more-btn"
          onClick={handleLoadMore}
          disabled={loadingMore}
        >
          {loadingMore ? '読み込み中...' : 'もっと見る'}
        </button>
      )}

      {isDetailModalOpen && selectedWord && (
        <div className="modal-overlay" onClick={() => setIsDetailModalOpen(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setIsDetailModalOpen(false)}>
              ×
            </button>
            <div className="word-detail-section">
              <div className="word-detail-title">
                <span className="word-detail-lemma">{selectedWord.lemma}</span>
                {selectedWord.rank && (
                  <span className="rank-badge">順位 #{selectedWord.rank}</span>
                )}
              </div>

              <div className="word-detail-fields">
                <div className="word-detail-field">
                  <span className="field-label">読み</span>
                  <span className="field-value">{selectedWord.hiragana || '-'}</span>
                </div>
                <div className="word-detail-field">
                  <span className="field-label">活用形</span>
                  <span className="field-value">{selectedWord.lform || '-'}</span>
                </div>
                <div className="word-detail-field">
                  <span className="field-label">品詞</span>
                  <span className="field-value">{selectedWord.pos || '-'}</span>
                </div>
                <div className="word-detail-field">
                  <span className="field-label">種類</span>
                  <span className="field-value">{selectedWord.wtype || '-'}</span>
                </div>
                <div className="word-detail-field">
                  <span className="field-label">頻度</span>
                  <span className="field-value">{formatNumber(selectedWord.frequency)}</span>
                </div>
                <div className="word-detail-field">
                  <span className="field-label">100万語あたり</span>
                  <span className="field-value">{selectedWord.pmw ? selectedWord.pmw.toFixed(1) : '-'}</span>
                </div>
              </div>

              <div className="word-detail-actions">
                {renderAddButton(selectedWord)}
              </div>
            </div>
          </div>
        </div>
      )}

      {deckPickerWord && (
        <div className="modal-overlay" onClick={closeDeckPicker}>
          <div className="modal-content bccwj-deck-picker" onClick={e => e.stopPropagation()}>
            <button
              type="button"
              className="bccwj-deck-picker-close"
              onClick={closeDeckPicker}
              disabled={adding}
              aria-label="閉じる"
            >
              ×
            </button>
            <form className="bccwj-deck-picker-form" onSubmit={handleAddToDeck}>
              <h3>デッキに追加</h3>
              <p className="bccwj-deck-picker-word">{deckPickerWord.lemma}</p>
              {decks.length === 0 ? (
                <div className="bccwj-deck-picker-empty">
                  <p>デッキがありません。先にデッキを作成してください。</p>
                  <button type="button" className="bccwj-deck-picker-retry" onClick={loadDecks}>
                    再試行
                  </button>
                </div>
              ) : (
                <div className="bccwj-deck-picker-list" role="radiogroup" aria-label="デッキ">
                  {decks.map(deck => (
                    <label
                      key={deck.deck_id}
                      className={
                        selectedDeckId === deck.deck_id
                          ? 'bccwj-deck-option bccwj-deck-option-selected'
                          : 'bccwj-deck-option'
                      }
                    >
                      <input
                        type="radio"
                        name="bccwj-deck-picker"
                        value={deck.deck_id}
                        checked={selectedDeckId === deck.deck_id}
                        onChange={() => setSelectedDeckId(deck.deck_id)}
                      />
                      <span className="bccwj-deck-option-name">{deck.name}</span>
                      <span className="bccwj-deck-option-count">{deck.card_count} 枚</span>
                    </label>
                  ))}
                </div>
              )}
              {pickerError && <div className="bccwj-deck-picker-error">{pickerError}</div>}
              <div className="bccwj-deck-picker-actions">
                <button
                  type="button"
                  className="bccwj-deck-picker-cancel"
                  onClick={closeDeckPicker}
                  disabled={adding}
                >
                  キャンセル
                </button>
                <button
                  type="submit"
                  className="bccwj-deck-picker-submit"
                  disabled={adding || !selectedDeckId}
                >
                  {adding ? '追加中...' : 'デッキに追加する'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Fixed bottom bar: hit count + per-level stats stay visible while
          scrolling the grid (clearance padding lives on .bccwj-browser) */}
      <div className="bccwj-bottom-bar">
        <div className="bccwj-bottom-bar-inner">
          <span className="count-badge">
            {totalHits !== null
              ? totalHits === 0
                ? '0 語ヒット'
                : `${totalHits.toLocaleString()} 語ヒット（1〜${words.length} 件を表示中）`
              : `${words.length} 語を表示中`}
          </span>
          {Object.keys(statusCounts).length > 0 &&
            ['unrated', '1', '2', '3', '4'].map(key => (
              <span key={key} className="status-stat">
                {key === 'unrated'
                  ? `${BCCWJ_UNRATED_LABEL}: ${(statusCounts[key] ?? 0).toLocaleString()}`
                  : `${BCCWJ_MEMORY_STATUS_LABELS[Number(key) as BCCWJMemoryStatus]}: ${(statusCounts[key] ?? 0).toLocaleString()}`}
              </span>
            ))}
          {/* Numeric legend for the per-card status radios; labels come from the
              shared map so wording cannot drift from the badges/aria-labels */}
          <div className="status-radio-legend">
            {([1, 2, 3, 4] as BCCWJMemoryStatus[]).map(level => (
              <span key={level}>{level}={BCCWJ_MEMORY_STATUS_LABELS[level]}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default BCCWJBrowser